import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { QueryKey } from '@tanstack/react-query';
import { getScopedT } from 'helpers/translationHelpers';
import {
    getFilteredHistoricDataForModal,
    HistoricDataFilters,
} from './helpers/dataHelpers';
import { useGetHrSimulationHistoricData } from './helpers/hooks/valueByDate';
import ValueByDateGridLoader from './ValueByDateGridLoader';
import { getHistoricColDefs } from './helpers/colDefinitions';
import ValueByDateGrid from './ValueByDateGrid';
import { useTranslation } from 'react-i18next';

type Props = {
    hrSimulation: HrSimulation;
    apiCacheKey: QueryKey;
    endpoint: string;
    isNonRecurrentEnvelope?: boolean;
    filters: HistoricDataFilters;
    translationKey: string;
};

const HistoricDataGrid = ({
    hrSimulation,
    filters,
    isNonRecurrentEnvelope = false,
    apiCacheKey,
    endpoint,
    translationKey,
}: Props) => {
    const colDefs = getHistoricColDefs(hrSimulation.startDate);
    const { t } = useTranslation();
    const { data: historicDataResponse, isLoading: isHistoricDataLoading } =
        useGetHrSimulationHistoricData(apiCacheKey, endpoint);

    const tScoped = getScopedT(translationKey, t);

    const historicData = historicDataResponse?.data ?? [];

    const filteredHistoricData = getFilteredHistoricDataForModal({
        historicData,
        filters,
    });

    return (
        <>
            <Typography variant="h4">{tScoped('historicData')}</Typography>
            <Typography sx={{ pt: '0.6rem' }}>
                {tScoped(
                    `historicDataExplanation${isNonRecurrentEnvelope ? 'NonRecurrent' : ''}`,
                )}
            </Typography>
            {isHistoricDataLoading ? (
                <ValueByDateGridLoader />
            ) : (
                <Box
                    sx={{
                        mt: '1em',
                        // with ag-theme-material and autoHeight, there is a 150px min-height by default, which is too much here
                        '& .ag-body .ag-body-viewport .ag-center-cols-viewport':
                            {
                                minHeight: '3rem',
                            },
                    }}
                >
                    <ValueByDateGrid
                        className="ag-theme-material"
                        headerHeight={36}
                        rowHeight={36}
                        domLayout="autoHeight"
                        rowData={filteredHistoricData}
                        columnDefs={colDefs}
                        defaultColDef={{
                            suppressMovable: true,
                            suppressHeaderMenuButton: true,
                            width: 120,
                        }}
                    />
                </Box>
            )}
        </>
    );
};
export default memo(HistoricDataGrid);
